// ComboboxList: the list every typeahead opens, the field picker's and the @mention composer's
// alike. An overlay on the track tone that settles in from its field and fades out; the row
// under the keyboard or the pointer takes the gold tint and its words turn gold. Rows commit on
// mousedown so the pick beats the field's blur. docs/operations/design-standard.md, "Controls".
//
// Positioned inside the field's own box, not portalled: call-site tests find rows with
// within(card), and a portal would move them out of the card. A clipping ancestor therefore
// wins; useDropSide opens upward when the room is above, which is the part it can fix.

import React, { useEffect, useRef, useState } from 'react';
import { IconCheck } from './icons';
import { MS, usePresence } from './motion';
import Skeleton from './Skeleton';
import { Tag } from './Tag';

export interface ComboboxOption {
  /** Stable identity, handed back on pick. */
  id: string | number;
  /** The words a person types to find it. */
  label: string;
  /** A quieter second part: a status, a surname, the name behind a code. Searchable. */
  sublabel?: string | null;
  /** A short code at the row's end: a vendor code, a kind, "new". Searchable. */
  chip?: string | null;
  /** Rows in a run share a heading above the first of them. The caller's order makes the runs. */
  group?: string | null;
  /** Anything the caller wants back. */
  data?: unknown;
  /** Never filtered away and always last: an action row ("New item: brass grommet") on a search. */
  pinned?: boolean;
}

/** The list's cap, in pixels. Kept beside `max-h-60` so the flip and the class cannot drift. */
export const LIST_MAX_PX = 240;
const EDGE_PX = 8;

/**
 * What will cut the list off: the nearest scroll container, intersected with the window. An
 * `overflow-x` other than visible makes `overflow-y` clip too, which is how a table's last row
 * lost its list while the window still had room below.
 */
function clipBounds(el: HTMLElement): { top: number; bottom: number } {
  let top = 0;
  let bottom = window.innerHeight;
  let node = el.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    if (style.overflowY !== 'visible' || style.overflowX !== 'visible') {
      const box = node.getBoundingClientRect();
      top = Math.max(top, box.top);
      bottom = Math.min(bottom, box.bottom);
      break;
    }
    node = node.parentElement;
  }
  return { top: Math.max(top, 0), bottom: Math.min(bottom, window.innerHeight) };
}

export type DropSide = 'below' | 'above';

/**
 * Which side of `anchor` the list opens on. Measured against the list's cap, not its current
 * height, so a list that shrinks while someone types does not jump across the field.
 */
export function useDropSide(anchor: React.RefObject<HTMLElement>, open: boolean, prefer: DropSide | 'auto' = 'auto'): DropSide {
  const [side, setSide] = useState<DropSide>(prefer === 'above' ? 'above' : 'below');
  useEffect(() => {
    if (prefer !== 'auto') { setSide(prefer); return undefined; }
    if (!open) return undefined;
    const measure = () => {
      const el = anchor.current;
      if (!el) return;
      const box = el.getBoundingClientRect();
      const clip = clipBounds(el);
      const below = clip.bottom - box.bottom - EDGE_PX;
      const above = box.top - clip.top - EDGE_PX;
      // Up only when up is actually better; where both are cramped, down is what people expect.
      setSide(below < LIST_MAX_PX && above > below ? 'above' : 'below');
    };
    measure();
    window.addEventListener('resize', measure);
    // Capture, because a scroll inside the table holding the field does not bubble to window.
    window.addEventListener('scroll', measure, { capture: true, passive: true });
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefer]);
  return side;
}

export interface ComboboxListProps {
  /** The listbox id. Rows are `${id}-${index}`, for aria-activedescendant. */
  id: string;
  /** Names the list for a reader. */
  label?: string;
  options: ComboboxOption[];
  /** Whether the list is drawn. It fades out before it unmounts. */
  open: boolean;
  /** The row the keyboard is on. */
  active: number;
  onActive: (index: number) => void;
  onPick: (option: ComboboxOption) => void;
  side: DropSide;
  /** The current value: its row carries a check. */
  selectedId?: string | number | null;
  /** `stacked`: the sublabel under the label. `inline`: after it on one line (first name, surname). */
  layout?: 'stacked' | 'inline';
  /** Shown in the list's place when there are no rows: no match, nothing yet, why not. */
  message?: React.ReactNode;
  /** A search is in flight with nothing to show yet: the shape of rows stands in. */
  busy?: boolean;
}

interface Frame {
  options: ComboboxOption[];
  active: number;
  message?: React.ReactNode;
  busy?: boolean;
  side: DropSide;
}

export const ComboboxList: React.FC<ComboboxListProps> = ({
  id,
  label,
  options,
  open,
  active,
  onActive,
  onPick,
  side,
  selectedId,
  layout = 'stacked',
  message,
  busy,
}) => {
  const rows = options.length > 0;
  const showing = open && (rows || !!busy || !!message);
  const presence = usePresence(showing, MS.exit);
  // What leaves is what was last shown: the field resets its query as the list closes, and the
  // fading list must not flash back to every option on the way out.
  const frame = useRef<Frame>({ options, active, message, busy, side });
  if (showing) frame.current = { options, active, message, busy, side };
  const listRef = useRef<HTMLUListElement>(null);
  // A press already picked; the click that ends it must not pick again.
  const pressed = useRef(false);

  // Follow the keyboard past the fold.
  useEffect(() => {
    if (!showing || !listRef.current) return;
    const el = listRef.current.children[active] as HTMLElement | undefined;
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active, showing]);

  if (!presence.mounted) return null;
  const f = frame.current;
  const shown = showing && presence.open;
  const chosen = selectedId != null && f.options.some((o) => String(o.id) === String(selectedId));

  return (
    <div
      aria-hidden={showing ? undefined : true}
      aria-busy={f.busy || undefined}
      className={`absolute left-0 right-0 z-30 overflow-hidden rounded-overlay bg-surface-container-high shadow-overlay transition-[opacity,transform] motion-reduce:scale-100 motion-reduce:transition-[opacity] ${
        f.side === 'above' ? 'bottom-full mb-1 origin-bottom' : 'top-full mt-1 origin-top'
      } ${shown ? 'scale-100 opacity-100 duration-standard ease-emphasized' : 'pointer-events-none scale-[0.96] opacity-0 duration-exit ease-exit'}`}
    >
      {f.options.length > 0 ? (
        // Named apart from its field: a list sharing the field's name makes every label lookup find two.
        <ul ref={listRef} id={id} role="listbox" aria-label={label ? `${label} choices` : undefined} className="m-0 max-h-60 list-none overflow-y-auto p-1">
          {f.options.map((o, i) => {
            const on = i === f.active;
            const prev = f.options[i - 1];
            const startsGroup = !!o.group && (!prev || prev.group !== o.group);
            const startsActions = !!o.pinned && !!prev && !prev.pinned;
            return (
              <li key={`${o.id}`} id={`${id}-${i}`} role="option" aria-selected={on}>
                {startsGroup && (
                  // A rule between runs, never above the first: a line across the top reads as a lid.
                  <div className={`px-2 pb-1 pt-2 text-xs font-medium text-on-surface-variant ${i > 0 ? 'mt-1 border-t border-outline-variant' : ''}`}>
                    {o.group}
                  </div>
                )}
                {startsActions && !startsGroup && <div aria-hidden="true" className="mx-1 my-1 h-px bg-outline-variant" />}
                <button
                  type="button"
                  tabIndex={-1}
                  onMouseDown={(e) => { e.preventDefault(); pressed.current = true; onPick(o); }}
                  // Screen readers and voice control activate with a bare click, no press first.
                  onClick={(e) => {
                    if (pressed.current || e.detail > 0) { pressed.current = false; return; }
                    onPick(o);
                  }}
                  onMouseEnter={() => onActive(i)}
                  className={`flex min-h-8 w-full items-center gap-2 rounded-inner px-2 text-left text-sm transition-[background-color,color] duration-quick ease-emphasized [@media(pointer:coarse)]:min-h-10 ${
                    layout === 'stacked' && o.sublabel ? 'py-1.5' : 'py-1'
                  } ${on ? 'bg-primary/15 text-primary-ink' : 'text-on-surface'}`}
                >
                  <span className={`min-w-0 flex-1 ${layout === 'inline' ? 'flex items-baseline gap-1.5' : ''}`}>
                    <span className={`${layout === 'inline' ? 'flex-none' : 'block'} truncate ${o.sublabel ? 'font-medium' : ''}`}>{o.label}</span>
                    {o.sublabel && (
                      <span className={`${layout === 'inline' ? 'min-w-0' : 'block text-xs leading-4'} truncate ${on ? 'text-primary-ink' : 'text-on-surface-variant'}`}>
                        {o.sublabel}
                      </span>
                    )}
                  </span>
                  {o.chip && <Tag variant="code" size="sm" className="flex-none">{o.chip}</Tag>}
                  {chosen && (
                    <span className="flex h-4 w-4 flex-none items-center justify-center text-primary">
                      {String(o.id) === String(selectedId) && <IconCheck className="h-4 w-4" />}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      ) : f.busy ? (
        <div role="status" className="space-y-1 p-1">
          <span className="sr-only">Searching</span>
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
        </div>
      ) : (
        <div role="status" className="px-3 py-2.5 text-[13px] text-on-surface-variant">{f.message}</div>
      )}
    </div>
  );
};

export default ComboboxList;
